// deps
import React, { memo, useCallback, useContext } from "react";

// internal deps
import { BillDetailsBanner } from "@swiggy-private/core-ui";
import { AnalyticsContext } from "@swiggy-private/checkout-core";

// analytics
import { SCREEN_NAME } from "@cart-client/analytics/constants";

// types
import { PLBillBannerLineItem } from "@swiggy-private/cart-types";

// styles
import { BillDetailsBannerContainer } from "./styles";
import { isEmpty } from "@swiggy-private/js-utils";
import { PL_BILL_BANNER_ITEM_TEST_IDS } from "@swiggy-private/cart-test-ids";

interface BillBannerItemProps {
    item: PLBillBannerLineItem;
}

const BillBannerItem = ({ item }: BillBannerItemProps) => {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);

    const subtitleString = item.banner_details?.subtitle?.map((p) => (p.clickable ? `[[${p.text}]]` : p.text)).join("");

    const bannerAnalytics = item.banner_details?.analytics;
    const halfCardAnalytics = item.banner_details?.half_card?.analytics;

    const onImpressionBanner = useCallback(() => {
        if (!isEmpty(bannerAnalytics) && !isEmpty(bannerAnalytics.impression_action)) {
            impressionEvent({
                category: bannerAnalytics?.screen_name ?? SCREEN_NAME.CHECKOUT,
                action: bannerAnalytics?.impression_action,
                context: bannerAnalytics?.context,
            });
        }
    }, [bannerAnalytics, impressionEvent]);

    const onClickBanner = useCallback(() => {
        if (!isEmpty(bannerAnalytics) && !isEmpty(bannerAnalytics.click_action)) {
            clickEvent({
                category: bannerAnalytics?.screen_name ?? SCREEN_NAME.CHECKOUT,
                action: bannerAnalytics?.click_action,
                context: bannerAnalytics?.context,
            });
        }
    }, [bannerAnalytics, clickEvent]);

    const onImpressionBannerHalfcard = useCallback(() => {
        if (!isEmpty(halfCardAnalytics) && !isEmpty(halfCardAnalytics.impression_action)) {
            impressionEvent({
                category: halfCardAnalytics?.screen_name ?? SCREEN_NAME.CHECKOUT,
                action: halfCardAnalytics?.impression_action,
                context: halfCardAnalytics?.context,
            });
        }
    }, [halfCardAnalytics, impressionEvent]);

    const onClickCloseBannerHalfcard = useCallback(() => {
        if (!isEmpty(halfCardAnalytics) && !isEmpty(halfCardAnalytics.click_action)) {
            clickEvent({
                category: halfCardAnalytics?.screen_name ?? SCREEN_NAME.CHECKOUT,
                action: halfCardAnalytics?.click_action,
                context: halfCardAnalytics?.context,
            });
        }
    }, [halfCardAnalytics, clickEvent]);

    return (
        <BillDetailsBannerContainer data-testid={PL_BILL_BANNER_ITEM_TEST_IDS.CONTAINER}>
            <BillDetailsBanner
                bannerType={item.banner_details.banner_type}
                title={item.banner_details.title}
                subtitle={subtitleString}
                icon={item.banner_details.icon}
                titleColor={item.banner_details.title_color}
                subtitleColor={item.banner_details.subtitle_color}
                bannerBgColor={item.banner_details.banner_bg_color}
                halfCardData={item.banner_details.half_card}
                analytics={{
                    onImpressionBanner,
                    onClickBanner,
                    onImpressionBannerHalfcard,
                    onClickCloseBannerHalfcard,
                }}
            />
        </BillDetailsBannerContainer>
    );
};

export default memo(BillBannerItem);
